import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Row, Container } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import env from 'Env';
import { encodeSlug } from 'Helpers/Slug';
import { facetDesc, facetH1Title, facetSeoTitle, facetTitle } from 'Features/FacetCategory/components/facetSEO';
import { history } from 'Redux/store';
import { getFacetReviews } from 'Redux/ducks/facet';
import FacetBreadcrumbs from 'Components/Utility/SEO/FacetBreadcrumbs';
import FacetList from 'Components/Utility/SEO/FacetList';
import fetchFacetData from 'Api/Facet/facet-service';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import HitCard from 'Components/Cards/HitCard';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import NoResults from 'Components/NotFound/NoResults';
import Paginate from 'Components/Utility/Paginate';
import PillFilters from 'Components/Utility/PillFilters';
import SortByDropdown from 'Components/Utility/SortBy/SortByDropdown';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import NonGameBanner from 'Components/Banner/NonGameBanner';
import FacetBlogPosts from 'Features/FacetCategory/components/FacetBlogPosts';
import FacetSearchScrollBreak from 'Features/FacetCategory/components/FacetSearchScrollBreak';
import imgixConfig from 'Config/imgix';
import useViewport from 'Hooks/useViewport';
import sortByMessages from 'Helpers/Messages/sort-by-messages';
import messages from 'Helpers/Messages/category-messages';
import AsterixMidPageBanner from 'Features/FacetCategory/components/AsterixMidPageBanner';
import AsterixProducts from 'Features/FacetCategory/components/AsterixProducts';

import 'Components/Banner/CategoryPageBanner.scss';
import 'Features/FacetCategory/components/FacetBoxesList.scss';
import 'Features/FacetCategory/pages/FacetSearch.scss';

const MysteryBundleCardContainer = lazy(() => import(/* webpackChunkName: "MysteryBundleCard" */ 'Components/Cards/MysteryBundleCardContainer'));
const EmailSignupPlacement = lazy(() => import(/* webpackChunkName: "EmailSignupPlacement" */ 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement'));

const PRODUCT_COUNT_THRESHOLD = env.algolia.indexName === 'fan' ? 5 : 1;

const sortShortcuts = {
  '#latest': 'release_date_desc',
  '#price': 'price_asc',
};

// The function for fetching hits is returned by fetchFacetData
let fetchHitsFunc = null;

const FacetSearch = ({
  location: { search, hash },
  match: { params: { slug, facet } },
}) => {
  const { formatMessage } = useIntl();

  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [hitsObject, setHitsObject] = useState({ hits: null, facets: {} });
  const [facetData, setFacetData] = useState(null);
  const [relatedFacetData, setRelatedFacetData] = useState({});
  const [displayTypes, setDisplayTypes] = useState([]);
  const [name, setName] = useState('');
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(true);
  const [scrollBreakHitObject, setScrollBreakHitObject] = useState({ hits: null });
  const [hideScrollBreak, setHideScrollBreak] = useState(false);
  const { isAboveMd, isBelowMd } = useViewport();

  const [filterType, setFilterType] = useState('');

  const initialSort = sortShortcuts[hash] || '';
  const [sort, setSort] = useState(initialSort);

  const descriptionRef = useRef(null);

  const page = Number(getUrlParameter('page', search) || 1);

  const slugToSearch = encodeSlug(slug);

  const onFilterChange = (type) => {
    setFilterType(type);
    if (page !== 1) {
      history.replace(`/${facet}/${slugToSearch}`);
    }
  };

  const onSortByChange = (sortValue) => {
    setSort(sortValue);
    if (page !== 1) {
      history.replace(`/${facet}/${slugToSearch}`);
    }
  };

  // If the facet page matches a live sale, we want to replace the H1 of the page with what has been used in the sale record
  const liveSales = useSelector(state => state.sales.liveSales);
  let sale = {};
  if (!isEmpty(liveSales)) sale = liveSales.find(s => s.saleSlug === slugToSearch);
  const saleDisplayName = get(sale, 'displayName', '');

  // When page or filters change we just need to fetch hits, not all the facet data
  useEffect(() => {
    // fetchHitsFunc is tied to a slug, if for some reason it isn't going to fetch the right slug then skip it
    if (fetchHitsFunc && fetchHitsFunc.slug === slugToSearch) {
      setIsLoading(true);
      fetchHitsFunc(page, filterType, sort)
        .then((newHits) => {
          setHitsObject(newHits);
          setIsLoading(false);
        })
        .catch(setFetchError);
    }
  }, [page, filterType, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // If slug or facet changes, first fetch the facet data from api (might not always be there)
  useEffect(() => {
    setIsLoading(true);
    fetchFacetData(facet, slugToSearch)
      .then((res) => {
        fetchHitsFunc = res.fetch;

        // Then fetch the hits with the function returned (may be a different function for combo-collections)
        fetchHitsFunc(page, filterType, sort, true)
          .then((newHits) => {
            const runStateFuncs = () => {
              setFacetData(res.facetData);

              setRelatedFacetData(newHits.related || {});

              setDisplayTypes(newHits.displayTypes || []);

              setHitsObject(newHits);
              if (saleDisplayName) {
                setName(saleDisplayName);
              } else {
                setName(get(res.facetData, 'name', newHits.name));
              }
              setIsLoading(false);
            };

            if (window.locale === 'en' && page % 3 === 0 && !isEmpty(newHits.hits) && newHits.hits.length >= 18) {
              getFacetReviews(facet, slugToSearch)
                .then((results) => {
                  const scrollBreakData = get(results, 'data', null);
                  setScrollBreakHitObject(scrollBreakData);
                  runStateFuncs();
                })
                .catch(() => {
                  runStateFuncs();
                });
            } else {
              runStateFuncs();
            }
          })
          .catch(setFetchError);
      })
      .catch(setFetchError);
  }, [facet, slugToSearch, saleDisplayName]); // eslint-disable-line react-hooks/exhaustive-deps


  // When page or filters change we just need to fetch hits, not all the facet data
  useEffect(() => {
    if (window.locale === 'en' && page % 3 === 0 && !isEmpty(hitsObject.hits) && hitsObject.hits.length >= 18) {
      getFacetReviews(facet, slugToSearch)
        .then((results) => {
          const scrollBreakData = get(results, 'data', null);
          const scrollBreakHits = get(results, 'data.hits', null);

          if (!scrollBreakHits) setHideScrollBreak(true);

          setScrollBreakHitObject(scrollBreakData);
        })
        .catch(() => { });
    }
  }, [page, facet, slugToSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (fetchError) {
    throw fetchError;
  }

  const hits = hitsObject.hits;

  if (isLoading) {
    return <Loading />;
  }

  const redirect = slug !== encodeSlug(slug);

  if (redirect) {
    return <NotFound redirect={`${facet}/${encodeSlug(slug)}`} />;
  }

  if (get(facetData, 'redirect') && facetData.redirect !== `${facet}/${encodeSlug(slug)}`) {
    return <NotFound redirect={facetData.redirect} />;
  }

  if (get(facetData, 'hide')) {
    return <NotFound redirect={facet} />;
  }

  const resultsTooThin =
    page === 1 &&
    ((!hits || hits.length < PRODUCT_COUNT_THRESHOLD) && facet !== 'collections') &&
    !(facetData && facetData.always_show);

  if (resultsTooThin && !filterType) {
    return <NotFound />;
  }

  const h1Title = facetH1Title(formatMessage, name, hits, page, facet, saleDisplayName);

  const seoTitle = facetSeoTitle(formatMessage, name, hits, page);

  const description = facetDesc(formatMessage, name, facetData, facet, relatedFacetData, hits);
  const plainDescription =
    facetDesc(formatMessage, name, facetData, facet, relatedFacetData, hits, true);

  const headerImg = get(facetData, 'header_image');

  const filterOptions = [
    { name: formatMessage(messages.all_word), type: '' },
    { name: formatMessage(messages.games), type: 'game' },
    { name: formatMessage(messages.dlc), type: 'dlc' },
  ];

  const soryByOptions = [
    { name: formatMessage(sortByMessages.topSellers), value: '' },
    { name: formatMessage(sortByMessages.priceLowHigh), value: 'price_asc' },
    { name: formatMessage(sortByMessages.priceHighLow), value: 'price_desc' },
    { name: formatMessage(sortByMessages.alphabetical), value: 'name' },
    { name: formatMessage(sortByMessages.topDiscount), value: 'discount' },
    { name: formatMessage(sortByMessages.latestDeals), value: 'latest_deals' },
    { name: formatMessage(sortByMessages.endingSoon), value: 'ending_soon' },
    { name: formatMessage(sortByMessages.mostWanted), value: 'most_wanted' },
    { name: formatMessage(sortByMessages.releaseDate), value: 'release_date_desc' },
    { name: formatMessage(sortByMessages.topRated), value: 'top_rated' },
  ];

  const showTypeFilters = displayTypes.includes('game') && displayTypes.includes('dlc');
  const showScrollBreak = isBelowMd && !hideScrollBreak && page % 3 === 0 && !isEmpty(hits) && hits.length >= 18 && window.locale === 'en';

  const bgImgSrc = `${env.cdnUrl}/product/original/${headerImg}`;

  const fantasyVersePublisherSlugs = [
    'medibang-inc',
    'dark-horse-comics',
    'american-mythology',
    'dark-wave-comics',
    't-pub-ltd',
    'karl-drinkwater',
    'boss-fight-books',
    'nbm-publishing',
    'an-inkwell-of-nectar',
    'austin-dragon',
    'michael-la-ronn',
    'katie-cross',
  ];

  const fantasyVersePublisher = includes(fantasyVersePublisherSlugs, slug);

  const showFourColumns = isAboveMd && !fantasyVersePublisher;

  let showCompetitionBanner = false;
  const startDate = '2025-07-11T09:00:00.000Z'; // 10am UK
  const endDate = '2025-07-18T22:59:00.000Z'; // 11:59pm UK

  if (Date.now() > Date.parse(startDate) && Date.now() < Date.parse(endDate)) {
    showCompetitionBanner = true;
  }

  return (
    <main className="FacetSearchPage">
      <Meta
        title={seoTitle}
        description={plainDescription}
        url={`${facet}/${slug}`}
        hits={hits}
        nbHits={hitsObject.nbHits}
        hitsPerPage={hitsObject.hitsPerPage}
        pageType="CollectionPage"
        pageName={h1Title}
      />
      {isEmpty(hits) && hits !== null && !(facetData && facetData.always_show) ?
        <NotFound />
        :
        <React.Fragment>
          <FacetBreadcrumbs
            facet={facet}
            parent={get(facetData, 'parent[0]')}
            slug={slug}
            name={name}
            page={page}
          />
          {facet !== 'genres' && headerImg &&
            <header className="facet-header-img-container">
              <img
                src={`${bgImgSrc}?${imgixConfig}&w=1800&h=200&fit=crop`}
                srcSet={`
                ${bgImgSrc}?${imgixConfig}&w=5000&h=400&fit=crop 5000w,
                ${bgImgSrc}?${imgixConfig}&w=4250&h=340&fit=crop 4250w,
                ${bgImgSrc}?${imgixConfig}&w=3600&h=285&fit=crop 3600w,
                ${bgImgSrc}?${imgixConfig}&w=2500&h=200&fit=crop 2500w,
                ${bgImgSrc}?${imgixConfig}&w=1800&h=200&fit=crop 1800w,
                ${bgImgSrc}?${imgixConfig}&w=1520&h=179&fit=crop 1520w,
                ${bgImgSrc}?${imgixConfig}&w=1200&h=143&fit=crop 1200w,
                ${bgImgSrc}?${imgixConfig}&w=992&h=107&fit=crop 992w,
                ${bgImgSrc}?${imgixConfig}&w=768&h=79&fit=crop 768w
                `}
                alt={name}
              />
            </header>
          }

          <Container>
            <div className="FacetSearchPage__header">
              {facet === 'franchises' && slug === 'asterix-obelix' &&
                <div className="FacetSearchPage__asterixSection">
                  <AsterixProducts />
                  <AsterixMidPageBanner />
                </div>
              }
              {facet === 'categories' && slug === 'mystery' &&
                <Suspense>
                  <MysteryBundleCardContainer all />
                </Suspense>
              }
              <h1 className="pt-5">{h1Title}</h1>
              <div className={classNames('FacetSearchPage__description', { 'FacetSearchPage__description--collapsed': descriptionCollapsed })} ref={descriptionRef}>
                <p className="disguise-links seo-blurb-text">
                  {description}
                  {facetData && facetData.related_desc &&
                    <React.Fragment>
                      &nbsp;
                      <FormattedMessage
                        id="facet-search.games-like"
                        defaultMessage="If you like playing {gameName} games, why not take a look at other {link}"
                        values={{
                          gameName: name,
                          link: <Link to={`/games-like/${facetData.slug}`} className="visible-link">
                            <FormattedMessage
                              id="facet-search.games-like.link"
                              defaultMessage="games like {gameName}."
                              values={{
                                gameName: name,
                              }}
                            />
                          </Link>,
                        }}
                      />
                    </React.Fragment>
                  }
                </p>
                {descriptionCollapsed &&
                  <div className="FacetSearchPage__description__gradient" />
                }
              </div>
              <button className="FacetSearchPage__morebutton" onClick={() => { setDescriptionCollapsed(!descriptionCollapsed); }}>
                {descriptionCollapsed &&
                  <>
                    <FormattedMessage id="facet-search.description.show-more" defaultMessage="Show more" />
                    <FontAwesomeIcon icon={faChevronDown} />
                  </>
                }
                {!descriptionCollapsed &&
                  <>
                    <FormattedMessage id="facet-search.description.show-less" defaultMessage="Show less" />
                    <FontAwesomeIcon icon={faChevronUp} />
                  </>
                }
              </button>
            </div>
            {!isEmpty(hits) &&
              <div className={`categories-and-sortby-section ${showTypeFilters ? '' : 'justify-content-end'}`}>
                {showTypeFilters &&
                  <PillFilters
                    mobilePills
                    options={filterOptions}
                    value={filterType}
                    onChange={onFilterChange}
                  />
                }

                <SortByDropdown
                  options={soryByOptions}
                  value={sort}
                  onChange={onSortByChange}
                />
              </div>
            }

            {facet === 'collections' && slug === 'programmers-day' &&
              <Row className="mb-5 pb-2">
                <NonGameBanner />
              </Row>
            }

            {hits === null &&
              <div className="row">
                <SkeletonHitCard
                  numberOfCards={36}
                  lgSize={4}
                  smSize={6}
                />
              </div>
            }

            {!isEmpty(hits) && showScrollBreak &&
              <>
                <ul className="HitCardsRow row">
                  {hits.slice(0, 16).map((hit, i) =>
                    <HitCard
                      key={hit.objectID}
                      hit={hit}
                      lgSize={fantasyVersePublisher || showFourColumns ? 3 : 4}
                      smSize={6}
                      lazy={i >= 6}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                      coverTypeOverride={fantasyVersePublisher ? 'portrait' : null}
                      adaptiveSize={fantasyVersePublisher}
                    />,
                  )}
                </ul>
                <FacetSearchScrollBreak hits={scrollBreakHitObject.hits} />
                <ul className="HitCardsRow row">
                  {hits.slice(16).map(hit =>
                    <HitCard
                      lazy
                      key={hit.objectID}
                      hit={hit}
                      lgSize={fantasyVersePublisher || showFourColumns ? 3 : 4}
                      smSize={6}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                      coverTypeOverride={fantasyVersePublisher ? 'portrait' : null}
                      adaptiveSize={fantasyVersePublisher}
                    />,
                  )}
                </ul>
              </>
            }

            {!isEmpty(hits) && !showScrollBreak &&
              <>
                <ul className="HitCardsRow row">
                  {hits.map((hit, i) =>
                    <HitCard
                      key={hit.objectID}
                      hit={hit}
                      lgSize={fantasyVersePublisher || showFourColumns ? 3 : 4}
                      smSize={6}
                      lazy={i >= 6}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                      coverTypeOverride={fantasyVersePublisher ? 'portrait' : null}
                      adaptiveSize={fantasyVersePublisher}
                    />,
                  )}
                </ul>
              </>
            }

            {isEmpty(hits) && facetData && facetData.always_show &&
              <NoResults pageName={h1Title} />
            }

            <Row>
              <Paginate hits={hitsObject} />
            </Row>

            {facet === 'franchises' && slug === 'rimworld' && showCompetitionBanner &&
              <EmailSignupPlacement appearsOn="rimworld" />
            }

            {facetData && facetData.children && !isEmpty(facetData.children) &&
              <section className="quick-links mt-5 mb-3">
                <FacetList facet={facet} facets={sortBy(facetData.children, ['name'])} name={`${name} Sub-categories`} />
                <div className="quick-links">
                  <ul className="quicklinks-container">
                    {sortBy(facetData.children, ['name']).map(a =>
                      <li className="quick-link" key={a.slug}>
                        <Link to={`/${facet}/${a.slug}`}>
                          <div key={a.slug}>
                            <p>{a.name}</p>
                          </div>
                        </Link>
                      </li>,
                    )}
                  </ul>
                </div>
              </section>
            }

            {relatedFacetData &&
              relatedFacetData.franchises &&
              !!relatedFacetData.franchises.length &&
              <section className="mt-5 mb-5">
                <div>
                  <h2 className="h3 pb-3">
                    <FormattedMessage id="facet-search.franchises" defaultMessage="Top {genre} Franchises" values={{ genre: name }} />
                    <span>
                      <Link to={'/franchises'}>
                        <button className="view-all-btn btn btn-outline-primary btn-sm">View All</button>
                      </Link>
                    </span>
                  </h2>
                </div>

                {relatedFacetData &&
                  relatedFacetData.franchises &&
                  !!relatedFacetData.franchises.length &&
                  <div className="quick-links">
                    <ul className="quicklinks-container">
                      <FacetList name={`${name} Franchises`} facet="franchises" facets={relatedFacetData.franchises.slice(0, 15)} />
                      {relatedFacetData.franchises.slice(0, 15).map(a =>
                        <li key={a.slug} className="quick-link">
                          <Link to={`/franchises/${a.slug}`}>
                            <div key={a.slug}>
                              <p>{a.name}</p>
                            </div>
                          </Link>
                        </li>,
                      )}
                    </ul>
                  </div>
                }
              </section>
            }

            {relatedFacetData &&
              relatedFacetData.categories &&
              !!relatedFacetData.categories.length &&
              <section className="mt-5 mb-5">
                <div>
                  <h2 className="h3 pb-3">
                    <FormattedMessage id="facet-search.categories" defaultMessage="Top {title} Genres" values={{ title: name }} />
                    <span>
                      <Link to={'/categories'}>
                        <button className="view-all-btn btn btn-outline-primary btn-sm">View All</button>
                      </Link>
                    </span>
                  </h2>
                </div>

                {relatedFacetData &&
                  relatedFacetData.categories &&
                  !!relatedFacetData.categories.length &&
                  <div className="quick-links">
                    <ul className="quicklinks-container">
                      <FacetList name={`${name} Genres`} facet="categories" facets={relatedFacetData.categories.slice(0, 15)} />
                      {relatedFacetData.categories.slice(0, 15).map(a =>
                        <li key={a.slug} className="quick-link">
                          <Link to={`/categories/${a.slug}`}>
                            <div key={a.slug}>
                              <p>{a.name}</p>
                            </div>
                          </Link>
                        </li>,
                      )}
                    </ul>
                  </div>
                }
              </section>
            }

            {window.locale === 'en' && hits !== null &&
              <FacetBlogPosts
                slug={slug}
                facet={get(facetData, 'facet', facet)}
                hitSlugs={hits.map(h => h.slug)}
                title={facetTitle(formatMessage, name, hits)}
              />
            }
          </Container>
        </React.Fragment>
      }
    </main>
  );
};

FacetSearch.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
      facet: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

FacetSearch.defaultProps = {
  facet: 'publishers',
};

export default FacetSearch;
